"use client";
import React, { useEffect, useState } from "react";
import {
  TrendingSection,
  TrendingWalletCard,
  TrendingCharityCard,
  TrendingEventCard,
} from "../../components/trending/TrendingSection";
import type { TrendingResponse } from "../../pages/api/trending";
import Footer from "../components/Footer";
import {
  Badge,
  GradientText,
  DividerRow,
  ScrollReveal,
  AnimatedCounter,
  Pill,
} from "../../components/shared";
import type { DividerRowItem } from "../../components/shared";
import {
  FlameIcon,
  WalletIcon,
  SearchIcon,
  ShieldAlertIcon,
  HandHeartIcon,
  MegaphoneIcon,
  AlertTriangleIcon,
} from "../../components/shared/icons";

/**
 * Trending page, restyled to match the rest of the Reqrium dashboard
 * (glass hero panel, gradient headline, shared icon/badge system) per the
 * client-provided mockup's intent for this route. The data layer is
 * untouched: same /api/trending fetch, same TrendingSection/TrendingWalletCard
 * /TrendingCharityCard/TrendingEventCard contracts, same five real sections.
 *
 * Two real fixes folded in here: (1) this page used to import and render its
 * own <Navbar />, double-rendering the nav since app/components/Chrome.tsx
 * already mounts it globally for every non-admin route - removed. (2) the
 * hero's stat strip now shows real counts derived from the fetched payload
 * (wallets/flags/charities/fundraisers in view) instead of inventing
 * marketing numbers the way the mockup's static "Active Now: 12,842" did.
 */
export default function TrendingPage() {
  const [data, setData] = useState<TrendingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch("/api/trending");
        if (!res.ok) {
          throw new Error(`Request failed with ${res.status}`);
        }
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load trending data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const wallets = data?.wallets ?? [];
  const mostSearched = data?.mostSearchedWallets ?? [];
  const mostFlagged = data?.mostFlaggedWallets ?? [];
  const charities = data?.charities ?? [];
  const events = data?.events ?? [];

  // Real counts straight off the fetched payload - nothing fabricated.
  const totalFlags = wallets.reduce((sum, w) => sum + (w.flagsCount || 0), 0);

  const statItems: DividerRowItem[] = [
    {
      key: "wallets",
      content: (
        <>
          <WalletIcon size={18} className="shrink-0" style={{ color: "#00D4FF" }} />
          <span className="min-w-0">
            <AnimatedCounter value={wallets.length} className="block text-lg font-extrabold leading-tight text-[#f1f5f9]" />
            <span className="block text-xs font-medium leading-tight" style={{ color: "var(--muted-text, #94a3b8)" }}>
              Wallets Trending
            </span>
          </span>
        </>
      ),
    },
    {
      key: "flags",
      content: (
        <>
          <ShieldAlertIcon size={18} className="shrink-0" style={{ color: "#F87171" }} />
          <span className="min-w-0">
            <AnimatedCounter value={totalFlags} className="block text-lg font-extrabold leading-tight text-[#f1f5f9]" />
            <span className="block text-xs font-medium leading-tight" style={{ color: "var(--muted-text, #94a3b8)" }}>
              Active Flags
            </span>
          </span>
        </>
      ),
    },
    {
      key: "charities",
      content: (
        <>
          <HandHeartIcon size={18} className="shrink-0" style={{ color: "#A855F7" }} />
          <span className="min-w-0">
            <AnimatedCounter value={charities.length} className="block text-lg font-extrabold leading-tight text-[#f1f5f9]" />
            <span className="block text-xs font-medium leading-tight" style={{ color: "var(--muted-text, #94a3b8)" }}>
              Charities Rising
            </span>
          </span>
        </>
      ),
    },
    {
      key: "fundraisers",
      content: (
        <>
          <MegaphoneIcon size={18} className="shrink-0" style={{ color: "#F97316" }} />
          <span className="min-w-0">
            <AnimatedCounter value={events.length} className="block text-lg font-extrabold leading-tight text-[#f1f5f9]" />
            <span className="block text-xs font-medium leading-tight" style={{ color: "var(--muted-text, #94a3b8)" }}>
              Fundraisers Active
            </span>
          </span>
        </>
      ),
    },
  ];

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{
        backgroundColor: "#0A0F1C",
        backgroundImage:
          "radial-gradient(circle at top, rgba(0,212,255,0.07), transparent 55%), radial-gradient(circle at bottom, rgba(249,115,22,0.06), transparent 55%)",
      }}
    >
      <main id="content" className="flex-1 w-full flex flex-col items-center">
        <section aria-labelledby="trending-heading" className="relative w-full px-4 pt-24 pb-4 sm:pt-28">
          <div className="mx-auto max-w-6xl">
            <ScrollReveal>
              <div
                className="relative overflow-hidden rounded-[28px] border px-5 py-8 sm:px-10 sm:py-10"
                style={{
                  borderColor: "rgba(91,127,255,0.28)",
                  background: "linear-gradient(180deg, rgba(16,24,39,0.75), rgba(10,15,28,0.9))",
                  boxShadow:
                    "0 0 0 1px rgba(0,212,255,0.08) inset, 0 30px 70px -30px rgba(0,0,0,0.8), 0 0 60px -20px rgba(249,115,22,0.22)",
                }}
              >
                {/* Ambient background glow */}
                <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
                  <div
                    className="absolute -top-24 left-1/3 h-72 w-72 rounded-full opacity-40 blur-3xl"
                    style={{ background: "radial-gradient(circle, rgba(0,212,255,0.25), transparent 70%)" }}
                  />
                  <div
                    className="absolute bottom-0 right-0 h-72 w-72 rounded-full opacity-30 blur-3xl"
                    style={{ background: "radial-gradient(circle, rgba(249,115,22,0.22), transparent 70%)" }}
                  />
                </div>

                <div className="relative z-10">
                  <Badge
                    icon={<FlameIcon size={14} />}
                    borderColor="rgba(249,115,22,0.4)"
                    backgroundColor="rgba(249,115,22,0.1)"
                    textColor="#fb923c"
                  >
                    Live · Updated Continuously
                  </Badge>

                  <h1 id="trending-heading" className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
                    <GradientText variant="brand">Trending</GradientText>
                  </h1>
                  <p className="mt-3 max-w-2xl text-base sm:text-lg leading-relaxed" style={{ color: "var(--muted-text, #94a3b8)" }}>
                    Live view of the wallets, charities, and fundraisers getting the most attention
                    across the network right now.
                  </p>
                </div>

                <DividerRow
                  className="relative z-10 mt-10"
                  background="rgba(8,12,22,0.5)"
                  cellClassName="flex flex-1 items-center min-w-0 gap-2.5 px-4 py-3.5"
                  items={statItems}
                />
              </div>
            </ScrollReveal>
          </div>
        </section>

        <div className="w-full px-4 mb-8">
          <div className="mx-auto max-w-6xl flex gap-2 overflow-x-auto pb-1">
            <Pill label="Trending Wallets" icon={<WalletIcon size={15} />} href="#wallets" />
            <Pill label="Most Searched" icon={<SearchIcon size={15} />} href="#most-searched" />
            <Pill label="Most Flagged" icon={<ShieldAlertIcon size={15} />} href="#most-flagged" />
            <Pill label="Charities" icon={<HandHeartIcon size={15} />} href="#charities" />
            <Pill label="Fundraisers" icon={<MegaphoneIcon size={15} />} href="#fundraisers" />
          </div>
        </div>

        {error && (
          <div className="w-full px-4">
            <div
              className="mx-auto mb-8 max-w-6xl flex items-start gap-3 rounded-2xl border p-4 text-sm"
              style={{ borderColor: "rgba(248,113,113,0.35)", background: "rgba(127,29,29,0.18)", color: "#fecaca" }}
            >
              <AlertTriangleIcon size={18} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}

        <div className="w-full px-4">
          <TrendingSection
            id="wallets"
            title="Trending Wallets"
            subtitle="Highly flagged or active wallets that the community is watching."
            icon={WalletIcon}
            accent="#00D4FF"
            viewAllHref="/wallet-analysis"
            loading={loading}
            items={wallets.map((w) => (
              <TrendingWalletCard key={w.address + w.chain} {...w} />
            ))}
          />

          <TrendingSection
            id="most-searched"
            title="Most Searched Wallets"
            subtitle="Addresses with the highest search counts across Reqrium."
            icon={SearchIcon}
            accent="#3B82F6"
            viewAllHref="/wallet-analysis"
            loading={loading}
            items={mostSearched.map((w) => (
              <TrendingWalletCard key={"search-" + w.address + w.chain} {...w} />
            ))}
          />

          <TrendingSection
            id="most-flagged"
            title="Most Flagged Wallets"
            subtitle="Wallets drawing the most moderation attention from the community."
            icon={ShieldAlertIcon}
            accent="#F87171"
            viewAllHref="/wallet-analysis"
            loading={loading}
            items={mostFlagged.map((w) => (
              <TrendingWalletCard key={"flag-" + w.address + w.chain} {...w} />
            ))}
          />

          <TrendingSection
            id="charities"
            title="Rising Charities"
            subtitle="Charities and organizations recently added or actively receiving support."
            icon={HandHeartIcon}
            accent="#A855F7"
            viewAllHref="/charities"
            loading={loading}
            items={charities.map((c) => (
              <TrendingCharityCard key={c.id} {...c} />
            ))}
          />

          <TrendingSection
            id="fundraisers"
            title="Active Fundraisers"
            subtitle="Fundraising campaigns approaching deadlines or gaining traction."
            icon={MegaphoneIcon}
            accent="#F97316"
            viewAllHref="/fundraisers"
            loading={loading}
            items={events.map((e) => (
              <TrendingEventCard key={e.id} {...e} />
            ))}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}
